/**
 * Eingabearten (Formate) der Analyse-Felder — Prüfung und Normalisierung.
 *
 * Warum ein `format`-Attribut und kein neuer `FieldType`: beide Renderer sind
 * if-Ketten mit stillem Text-Fallback (kein exhaustiver switch). Ein Typ, der
 * nur in einem Renderer implementiert wird, kompiliert grün und rendert im
 * anderen als nacktes Textfeld — genau der Fehlermodus, der die heutige
 * Divergenz erzeugt hat. Ein unbekanntes `format` degradiert dagegen sauber
 * auf das heutige Verhalten.
 *
 * Drei Invarianten, die diese Datei durchgängig einhält:
 *
 * 1. **Dependency-frei.** forms-core liegt im API-Image unter
 *    /packages/forms-core, die node_modules aber unter /app/node_modules —
 *    eine eigene Runtime-Dependency baut grün durch und killt dann den
 *    API-Start mit MODULE_NOT_FOUND. Deshalb auch kein `new URL()`:
 *    apps/api typprüft die QUELLE dieses Pakets (`types` zeigt auf src) mit
 *    target ES2021 ohne DOM-lib; die URL-Prüfung läuft über eine Regex.
 * 2. **Kein Datenverlust.** `normalize` gibt Unparsbares UNVERÄNDERT zurück.
 *    In PROD stehen in genau diesen Feldern fachlich gemeinte Freitexte
 *    ("1-2 Mio", "2300 p.A.", "Unterschiedlich"). Ein "parsen oder leeren"
 *    hätte sie gelöscht.
 * 3. **Kanonisch ist immer ein String**, nie eine JSON-Zahl: kein
 *    jsonb-Typwechsel, `Number(v)` funktioniert weiterhin, und "01067"
 *    bleibt "01067" statt zu 1067 zu werden.
 *
 * Leere Eingabe ist bei JEDEM Format gültig — Pflichtfelder sind ein eigenes
 * Thema und werden hier bewusst nicht mitentschieden.
 */
import { collectFields } from "./validation.js";
import { DEFAULT_DIAL_CODE, KNOWN_DIAL_CODES } from "./phoneCountries.js";
import { formatiereTelefon } from "./telefon.js";
const asText = (value) => String(value ?? "").trim();
const isEmpty = (value) => asText(value) === "";
/* ------------------------------------------------------------------ *
 * Telefon — "+49 221 12345"
 * ------------------------------------------------------------------ */
/**
 * Kanonisch ist die Schreibweise der PO-Spezifikation vom 18.08.2026:
 * Ländercode, Leerzeichen, Vorwahl, Leerzeichen, Nummer — ausdrücklich NICHT
 * E.164 ("+4922112345" ohne Trenner). Ein früherer Stand dieses Pakets
 * speicherte E.164; das war die Umsetzung des alten Plans und ist hier
 * abgelöst.
 *
 * Die Gruppierung wird NICHT erfunden. "+4922112345" wird zu "+49 22112345"
 * und nicht zu "+49 221 12345": die deutsche Vorwahllänge ist zwei- bis
 * fünfstellig und ohne Vorwahltabelle nicht bestimmbar, und eine geratene
 * Trennung wäre eine erfundene Aussage über die Nummer. Übernommen wird die
 * Gruppierung, die in der Eingabe steht.
 */
const PHONE_SHAPE = /^\+\d{1,3}(?: \d+)+(?:-\d+)?$/;
/** E.164 begrenzt die Gesamtlänge (Ländercode + Rufnummer) auf 15 Ziffern. */
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;
/**
 * Zerlegt eine geschriebene Nummer in Ländervorwahl und Rufnummer — das
 * Herzstück der Copy-Paste-Erkennung ("+49 221 12345", "0221 12345",
 * "0049221…" landen alle in denselben zwei Teilen).
 *
 * Bewusst OHNE Längenprüfung: eine unvollständige Eingabe ("0221") soll sich
 * im Feld trotzdem korrekt auf Vorwahl und Nummer verteilen. Ob das Ergebnis
 * eine gültige Rufnummer ist, entscheidet {@link isValidPhoneCore}.
 *
 * Drei Dinge tut die Funktion ausdrücklich NICHT:
 *
 * a) Sie **erfindet keine Ländervorwahl.** Eine Ziffernfolge ohne "+", "00"
 *    und ohne führende 0 ("1234567" — eine Durchwahl, eine Nebenstelle, eine
 *    versehentlich ins Telefonfeld getippte Kundennummer) bleibt unerkannt.
 *    Ein früherer Stand setzte "+49" davor; das machte aus Schmutz eine formal
 *    gültige, aber nicht wählbare Nummer, die danach nicht mehr als Schmutz
 *    erkennbar war.
 * b) Sie **lässt die Amtsnull nicht stehen** — und zwar hinter JEDER
 *    Ländervorwahl, nicht nur hinter +49. "+43 0664 1234567" wurde vorher zu
 *    "+4306641234567" und galt als gültig: dieselbe "formal gültig, nicht
 *    wählbar"-Krankheit wie das behobene "+49 (0) 221". Sobald ein Ländercode
 *    davorsteht, entfällt die 0 (Spezifikation) — das gilt für die geklammerte
 *    ("(0)") wie für die nackte Form.
 * c) Sie **glättet keinen Unsinn.** Die geklammerte Null wird nur direkt hinter
 *    der Ländervorwahl entfernt, nicht global: "00 (0) 49 221 12345" bleibt
 *    unerkannt, statt still zu "+49 221 12345" zu werden.
 */
export function splitPhone(input, defaultDialCode = DEFAULT_DIAL_CODE) {
    const raw = asText(input);
    if (!raw)
        return undefined;
    // Bindestrich, Schrägstrich und Punkt sind im deutschen Schreibgebrauch
    // GRUPPEN-Trenner derselben Nummer ("0221 12345-6" ist EINE Nummer, keine
    // Extension) — sie werden zu einfachen Leerzeichen, nicht entfernt. Ein
    // entfernter Bindestrich zöge "12345-6" zu "123456" zusammen und schriebe
    // damit still eine Gruppierung um, die der Nutzer geschrieben hat.
    //
    // Eine geklammerte Vorwahl am Anfang ("(0221) 12345") ist die klassische
    // Briefkopf-Schreibweise; sie wird aufgelöst, bevor über die Schreibweise
    // (international/national) entschieden wird — sonst fiele sie durch.
    const s = raw
        .replace(/[\u00A0\u202F]/g, " ")
        .replace(/[-\u2010-\u2015/.]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^\(\s*(\d+)\s*\)/, "$1");
    let dialCode;
    let national;
    if (s.startsWith("+") || s.startsWith("00")) {
        const body = (s.startsWith("+") ? s.slice(1) : s.slice(2)).replace(/^\s+/, "");
        const lead = body.match(/^\d{1,3}/);
        if (!lead)
            return undefined;
        // Längste bekannte Vorwahl gewinnt: "+1 212…" ist die 1, "+385 1…" die 385.
        let found;
        for (let len = lead[0].length; len >= 1; len--) {
            const candidate = lead[0].slice(0, len);
            if (KNOWN_DIAL_CODES.has(candidate)) {
                found = candidate;
                break;
            }
        }
        if (!found)
            return undefined;
        dialCode = found;
        national = body.slice(found.length);
    }
    else if (s.startsWith("0")) {
        // Nationale Schreibweise: die Amtsnull entfällt, die Vorwahl kommt aus dem
        // Feld (Default Deutschland) — sie wird nicht aus der Nummer geraten.
        dialCode = defaultDialCode.replace(/\D/g, "");
        national = s.slice(1);
        if (!KNOWN_DIAL_CODES.has(dialCode))
            return undefined;
    }
    else {
        return undefined;
    }
    // Die geklammerte Ziffer direkt hinter der Ländervorwahl wird ausgepackt,
    // nicht weggeworfen: "(0)" wird zu "0" und fällt danach der Amtsnull-Regel
    // zum Opfer, "(221)" bleibt als Vorwahl erhalten.
    national = national
        .replace(/^\s*\(\s*(\d+)\s*\)/, "$1")
        .replace(/^\s+/, "")
        .replace(/^0(?=[\d\s])/, "")
        .trim()
        .replace(/\s+/g, " ");
    // Alles, was jetzt noch stört (Buchstaben, restliche Klammern, ein zweites
    // "+"), macht die Eingabe unerkannt — nichts davon wird weggeworfen, der
    // Aufrufer behält den Rohwert.
    if (!/^[1-9]\d*(?: \d+)*$/.test(national))
        return undefined;
    return { dialCode, national };
}
/** "49" + "221 12345" → "+49 221 12345". */
export const formatPhone = (dialCode, national) => "+" +
    String(dialCode ?? "").replace(/\D/g, "") +
    " " +
    String(national ?? "").trim().replace(/\s+/g, " ");
const phoneDigits = (parts) => parts.dialCode.length + parts.national.replace(/\s/g, "").length;
const validParts = (input) => {
    const parts = splitPhone(input);
    if (!parts)
        return undefined;
    const digits = phoneDigits(parts);
    return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS ? parts : undefined;
};
/**
 * "0221/12345-6" → "+49 221 12345 6" · "+49 (0) 221 12345" → "+49 221 12345".
 *
 * Unvollständiges und Unparsbares kommt UNVERÄNDERT zurück (Invariante 2):
 * in genau diesen Feldern stehen in PROD fachlich gemeinte Freitexte
 * ("bitte über Frau Meier").
 */
export function normalizePhoneCore(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    // Seit 09/2026 dieselbe Schreibweise wie in jedem Telefonfeld der App
    // („+49 221 12345-67", siehe telefon.ts): Ortsvorwahl nach der Liste der
    // BNetzA getrennt, eine geschriebene Durchwahl bleibt erhalten.
    return validParts(raw) ? formatiereTelefon(raw, { geschaeftlich: true }) : raw;
}
export const isValidPhoneCore = (input) => isEmpty(input) || validParts(input) !== undefined;
/**
 * Ist die Zeichenkette bereits die kanonische Schreibweise? Prüft die FORM
 * (führendes "+", einfache Leerzeichen, keine Amtsnull) zusätzlich zur
 * Gültigkeit — damit ein Aufrufer erkennt, ob Normalisieren überhaupt etwas
 * ändern würde.
 */
export const isCanonicalPhone = (input) => {
    const s = asText(input);
    return PHONE_SHAPE.test(s) && isValidPhoneCore(s) && normalizePhoneCore(s) === s;
};
/**
 * Die öffentlichen Namen sind bewusst identisch zum Kern und tragen KEINEN
 * Modulzustand: eine prozessglobal registrierbare Engine würde dieselbe
 * Eingabe je nach Registrierung unterschiedlich bewerten (ein Node-Prozess
 * bedient alle Mandanten), und `validateAnswerFormats` wäre nicht mehr rein.
 */
export const normalizePhone = normalizePhoneCore;
export const isValidPhone = isValidPhoneCore;
/* ------------------------------------------------------------------ *
 * E-Mail
 * ------------------------------------------------------------------ */
/**
 * Bewusst pragmatisch statt RFC 5322: in PROD sind 5.947 von 5.961 Zeilen
 * gegen dieses Muster gültig, ein strengeres erzeugt nur Falschfehler.
 * Wortgleich zu apps/web/src/components/forms/validation/email.ts, damit das
 * Wording app-weit identisch bleibt.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Domain kleinschreiben, Local-Part NICHT — der ist laut RFC case-sensitiv. */
export function normalizeEmail(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    const at = raw.lastIndexOf("@");
    if (at < 0)
        return raw;
    return raw.slice(0, at) + "@" + raw.slice(at + 1).toLowerCase();
}
export const isValidEmail = (input) => isEmpty(input) || EMAIL_RE.test(asText(input));
/* ------------------------------------------------------------------ *
 * Website
 * ------------------------------------------------------------------ */
const URL_SCHEME_RE = /^([a-z][a-z0-9+.-]*):\/\//i;
/**
 * Der Host darf Nicht-ASCII enthalten: "münchen-versicherung.de" ist eine
 * reale Firmenadresse, und `url` meldet einen Fehler (severity "error"), der
 * das Abschicken blockiert — eine korrekt getippte Umlaut-Domain darf den
 * Nutzer nicht aussperren. Die Punycode-Form ("xn--...") passt ohnehin auf
 * dasselbe Muster; umgewandelt wird hier nichts (das braeuchte eine
 * Abhaengigkeit, und die Speicherform soll das bleiben, was der Nutzer
 * eingegeben hat).
 */
const URL_HOST_RE = /^([a-z0-9\u00a1-\uffff](?:[a-z0-9\u00a1-\uffff-]*[a-z0-9\u00a1-\uffff])?(?:\.[a-z0-9\u00a1-\uffff](?:[a-z0-9\u00a1-\uffff-]*[a-z0-9\u00a1-\uffff])?)*\.[a-z\u00a1-\uffff]{2,})(?::\d{1,5})?(?:[/?#].*)?$/i;
/** Gibt die schemalose Resthälfte zurück, oder `undefined` bei Unsinn. */
const parseUrlRest = (input) => {
    const raw = asText(input);
    if (!raw || /\s/.test(raw))
        return undefined;
    const scheme = raw.match(URL_SCHEME_RE);
    // Andere Schemata (mailto:, ftp:) sind in einem Website-Feld ein Fehler,
    // kein zu ergänzender Wert.
    if (scheme && !/^https?$/i.test(scheme[1]))
        return undefined;
    const rest = scheme
        ? raw.slice(scheme[0].length)
        : raw.replace(/^\/+/, ""); // protokoll-relative Pastes ("//beispiel.de")
    return URL_HOST_RE.test(rest) ? rest : undefined;
};
/**
 * "www.over-dach.com" → "https://www.over-dach.com".
 *
 * Bewusst wird NUR das Schema ergänzt: kein Kleinschreiben des Hosts, kein
 * Anhängen oder Abschneiden eines Trailing-Slash. Sonst wandert der Bestand
 * ohne Nutzeraktion — "https://cafe-blao.de/" (realer PROD-Wert) gälte beim
 * ersten Blur als Nutzeränderung, erzeugte Dirty-State, einen 450-ms-Autosave
 * ohne Nutzeraktion und einen scheinbaren Diff in der Auswertung.
 */
export function normalizeUrl(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    const rest = parseUrlRest(raw);
    if (rest === undefined)
        return raw;
    return URL_SCHEME_RE.test(raw) ? raw : "https://" + rest;
}
export const isValidUrl = (input) => isEmpty(input) || parseUrlRest(input) !== undefined;
/* ------------------------------------------------------------------ *
 * Zahlen, Geld, Prozent, Fläche
 * ------------------------------------------------------------------ */
/** €, %, m², qm, NBSP: Einheitenrauschen steht im Label, nicht im Wert. */
const UNIT_NOISE = /[€%\s\u00A0\u202F]/g;
/**
 * Liest deutsche UND englische Schreibweise und gibt die bereinigte
 * Zeichenkette zurueck (`^-?\d+(\.\d+)?$`) — oder `undefined`, wenn nichts
 * Zahlartiges uebrig bleibt.
 *
 * Regel bei zwei Trennerarten: der zuletzt stehende gewinnt als
 * Dezimaltrenner ("1.234,56" vs "1,234.56").
 *
 * Drei bewusste Wetten:
 *
 * 1. Bei nur Punkten gilt ausschliesslich `^-?\d{1,3}(\.\d{3})+$` als
 *    Tausendertrennung — "1.23" bleibt also 1,23 und wird nicht zu 123. Ein
 *    Dezimalwert mit exakt drei Nachkommastellen ("1.250" als 1,25 gemeint)
 *    wuerde dadurch um Faktor 1000 verfaelscht. In den Geldfeldern kommen
 *    drei Nachkommastellen praktisch nicht vor, gemischte Tausendertrennung
 *    ("50.000" neben "250000") dagegen belegt.
 * 2. Leerzeichen fallen ueberall weg, nicht nur am Rand: "1 500 000" wird zu
 *    1500000. Preis ist eine stille Falschannahme bei einer versehentlichen
 *    Aufzaehlung ("10 20" wird 1020). Die Alternative — Leerzeichen nur am
 *    Rand zu dulden — kostet mehr Falschfehler bei der gaengigen
 *    Tausenderschreibweise mit Leerzeichen, als sie gewinnt.
 * 3. Ein Vorzeichen bleibt erlaubt: "-5" ist eine gueltige Zahl. Fachlich
 *    unmoegliche negative Mitarbeiterzahlen und Flaechen sind Sache von
 *    `FieldDef.min` (eigenes Arbeitspaket), nicht des Formats — das Format
 *    beschreibt die Schreibweise, nicht den Wertebereich.
 */
export function cleanNumeric(input) {
    let s = asText(input);
    if (!s)
        return undefined;
    s = s
        .replace(/^(EUR|Euro|€)\s*/i, "")
        // Nachgestellte Waehrung ("1.500 EUR") und die kaufmaennische
        // Null-Cent-Schreibweise ("20.000,-") sind eindeutig und verlustfrei
        // aufloesbar. Ohne sie faellt der Betrag spaeter still aus der
        // Auswertung, weil `Number("20.000,-")` NaN ergibt.
        .replace(/\s*(EUR|Euro|€)$/i, "")
        .replace(/,\s*[-–—]{1,2}$/, "")
        .replace(/m²|m2|qm/gi, "")
        .replace(UNIT_NOISE, "");
    if (!s)
        return undefined;
    const comma = s.lastIndexOf(",");
    const dot = s.lastIndexOf(".");
    if (comma > -1 && dot > -1) {
        s = comma > dot ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
    }
    else if (comma > -1) {
        s = s.replace(",", ".");
    }
    else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
        s = s.replace(/\./g, "");
    }
    return /^-?\d+(\.\d+)?$/.test(s) ? s : undefined;
}
export function parseDecimal(input) {
    const s = cleanNumeric(input);
    if (s === undefined)
        return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
}
/** Ganzzahligkeit wird an der ZEICHENKETTE entschieden, nicht am Double:
 *  `Number("1.0000000000000000001")` ist 1 und damit faelschlich ganzzahlig. */
const isIntegerText = (cleaned) => !/\.\d*[1-9]/.test(cleaned);
/**
 * Kanonische Form direkt aus der bereinigten Zeichenkette — bewusst NICHT
 * ueber `String(Number(s))`. Der Umweg ueber das Double ist weder wertetreu
 * noch idempotent: "1000000000000000000000" wuerde zu "1e+21" und
 * "0,0000001" zu "1e-7" (beide danach selbst ungueltig), und
 * "1234567890123456789" verlore still seine letzten Ziffern. Das waere
 * Schmutz, den nicht der Bestand mitbringt, sondern dieser Code erzeugt.
 */
export function canonicalDecimal(cleaned) {
    const negativ = cleaned.startsWith("-");
    const [ganzRoh, bruchRoh = ""] = (negativ ? cleaned.slice(1) : cleaned).split(".");
    const ganz = ganzRoh.replace(/^0+(?=\d)/, "");
    const bruch = bruchRoh.replace(/0+$/, "");
    const betrag = bruch ? ganz + "." + bruch : ganz;
    // "-0" ist kein Wert, sondern ein Artefakt.
    return negativ && /[1-9]/.test(betrag) ? "-" + betrag : betrag;
}
/**
 * `decimals` bleibt optional und wird hier NICHT je Format vorbelegt: die
 * Nachkommastellen-Regel haengt am Feld (`FieldDef.decimals`, eigenes
 * Arbeitspaket), nicht am Format. Ohne sie bleibt "50.000" schlicht "50000"
 * statt "50000.00" — jedes erzwungene toFixed wuerde Bestandswerte anfassen,
 * ohne dass jemand etwas eingegeben hat.
 */
export function toCanonicalNumber(n, decimals) {
    return decimals === undefined ? String(n) : n.toFixed(decimals);
}
export function normalizeDecimal(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    const cleaned = cleanNumeric(raw);
    return cleaned === undefined ? raw : canonicalDecimal(cleaned);
}
export function normalizeInteger(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    const cleaned = cleanNumeric(raw);
    return cleaned === undefined || !isIntegerText(cleaned)
        ? raw
        : canonicalDecimal(cleaned);
}
export const isValidDecimal = (input) => isEmpty(input) || parseDecimal(input) !== undefined;
export const isValidInteger = (input) => {
    if (isEmpty(input))
        return true;
    const cleaned = cleanNumeric(input);
    return cleaned !== undefined && isIntegerText(cleaned);
};
/**
 * 0–100. Die Bereichsgrenze steckt hier im Format, weil `FieldDef.min/max`
 * in diesem Stand noch nicht existiert; ein Verstoß ist ohnehin nur eine
 * Warnung (PROD enthält einen Datensatz mit 152 % Gesellschafteranteilen,
 * und eine Teilerfassung ist fachlich legitim).
 */
export const isValidPercent = (input) => {
    if (isEmpty(input))
        return true;
    const n = parseDecimal(input);
    return n !== undefined && n >= 0 && n <= 100;
};
/* ------------------------------------------------------------------ *
 * Datum und Jahr
 * ------------------------------------------------------------------ */
const ISO_DATE_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
/**
 * Leerzeichen um die Trenner sind normale deutsche Schreibweise
 * ("12. 04. 1965") und werden geduldet. Ein ZWEISTELLIGES Jahr wird bewusst
 * NICHT geraten: eine Pivot-Regel muesste bei "01.01.26" zwischen 1926 und
 * 2026 entscheiden und wuerde bei einem Geburtsdatum still um 100 Jahre
 * danebenliegen. Solche Werte bleiben Rohwert und werden gemeldet.
 */
const DE_DATE_RE = /^(\d{1,2})\s*[./-]\s*(\d{1,2})\s*[./-]\s*(\d{4})\.?$/;
/** Kalendarisch, nicht nur syntaktisch: 31.02. ist falsch, 29.02.2024 nicht. */
const isRealDate = (y, m, d) => {
    if (m < 1 || m > 12 || d < 1 || d > 31)
        return false;
    const date = new Date(Date.UTC(y, m - 1, d));
    return (date.getUTCFullYear() === y &&
        date.getUTCMonth() === m - 1 &&
        date.getUTCDate() === d);
};
const pad2 = (n) => String(n).padStart(2, "0");
const parseDate = (input) => {
    const raw = asText(input);
    if (!raw)
        return undefined;
    const iso = raw.match(ISO_DATE_RE);
    const de = raw.match(DE_DATE_RE);
    const parts = iso
        ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
        : de
            ? [Number(de[3]), Number(de[2]), Number(de[1])]
            : undefined;
    if (!parts)
        return undefined;
    const [y, m, d] = parts;
    return isRealDate(y, m, d) ? y + "-" + pad2(m) + "-" + pad2(d) : undefined;
};
/**
 * "12.04.1965" → "1965-04-12". Bewusst ein ISO-STRING ohne Zeitanteil und
 * ohne Date-Objekt: die Speicherform bleibt ein String im jsonb, und eine
 * Zeitzone würde das Geburtsdatum um einen Tag verschieben.
 */
export function normalizeDate(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    return parseDate(raw) ?? raw;
}
export const isValidDate = (input) => isEmpty(input) || parseDate(input) !== undefined;
/**
 * Untergrenze für Gründungsjahre; die Obergrenze ist das laufende Jahr.
 * 1500 statt eines "plausiblen" 1800: die Prüfung soll Tippfehler wie "19965"
 * oder "65" fangen, nicht Firmengeschichte bewerten — Brauereien, Mühlen und
 * Gasthäuser mit Gründungsjahr vor 1800 sind selten, aber real, und der
 * Nutzer hätte bei einer Fehlermeldung keinen Ausweg (die Zahl stimmt ja).
 */
export const MIN_YEAR = 1500;
/**
 * Kanonisch ist das getrimmte "JJJJ". Belegte Bestandswerte wie "ca. 1960"
 * oder "1965/66" bleiben unverändert stehen — sie zu raten wäre erfunden.
 */
export function normalizeYear(input) {
    return asText(input);
}
/**
 * `maxYear` ist injizierbar, damit die Funktion rein bleibt und ein
 * Jahreswechsel keine Testergebnisse ohne Codeaenderung kippt. Die
 * Vorbelegung ist unveraendert das laufende Jahr.
 *
 * ACHTUNG fuer die Feld-Annotation: `year` meldet einen Fehler, und belegte
 * Bestandswerte wie "ca. 1960" oder "1965/66" verstossen dagegen. Die
 * Annotation darf deshalb erst zusammen mit der baseline-Degradierung
 * scharf geschaltet werden (nur in dieser Sitzung geaenderte Werte erzeugen
 * "error") — sonst blockiert schon das Oeffnen-und-Speichern.
 */
export const isValidYear = (input, maxYear = new Date().getUTCFullYear()) => {
    const raw = asText(input);
    if (!raw)
        return true;
    if (!/^\d{4}$/.test(raw))
        return false;
    const n = Number(raw);
    return n >= MIN_YEAR && n <= maxYear;
};
/* ------------------------------------------------------------------ *
 * PLZ, USt-IdNr, Handelsregisternummer
 * ------------------------------------------------------------------ */
/**
 * "D-50667" → "50667". Bewusst NICHT ziffernfressend: die Vorlage in
 * apps/web macht per replace(/\D/g, "") aus "50667 Köln" still "50667" und
 * wirft den Ort weg. Hier bleibt alles stehen, was nach dem Entfernen von
 * Ländervorsatz und Trennern keine reine 5-stellige Zahl ist.
 */
export function normalizePlz(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    const stripped = raw.replace(/^(DE|D)[\s-]*/i, "").replace(/[\s.-]/g, "");
    return /^\d{5}$/.test(stripped) ? stripped : raw;
}
/**
 * Deutscher Bereich 01067–99998; führende Null bleibt als String erhalten.
 *
 * ACHTUNG fuer die Feld-Annotation: oesterreichische ("1010") und Schweizer
 * ("8001") PLZ sind hier ungueltig, und `plz` meldet einen Fehler. Die
 * Standortfelder haben Auslandsbezug — die Bereichspruefung muss deshalb zur
 * WARNUNG herabgestuft werden, sobald das Feld "land" gefuellt und nicht
 * "Deutschland" ist. Das ist feldübergreifend und gehört nicht in eine reine
 * Einzelwert-Pruefung; es ist aber Voraussetzung dafuer, die PLZ-Felder
 * ueberhaupt zu annotieren.
 */
export const isValidPlz = (input) => {
    if (isEmpty(input))
        return true;
    const digits = normalizePlz(input);
    if (!/^\d{5}$/.test(digits))
        return false;
    const n = Number(digits);
    return n >= 1067 && n <= 99998;
};
/**
 * Trenner und Zero-Width-Zeichen aus Copy-Paste fallen weg; 9 nackte Ziffern
 * bekommen "DE" vorangestellt. Keine Prüfziffernrechnung — die würde bei
 * korrekt abgetippten Nummern nichts gewinnen und Falschfehler riskieren.
 */
export function normalizeUstIdNr(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    let s = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (/^\d{9}$/.test(s))
        s = "DE" + s;
    return /^DE\d{9}$/.test(s) ? s : raw;
}
export const isValidUstIdNr = (input) => isEmpty(input) || /^DE\d{9}$/.test(normalizeUstIdNr(input));
const HR_NUMBER_RE = /^\d{1,7}\s?[A-Za-z]?$/;
const HR_REGISTER_RE = /^(HRA|HRB|GnR|GsR|PR|VR)(?![A-Za-z])[\s./:-]*(?:([A-Za-z])[\s./:-]+)?(.*)$/i;
/**
 * "HRB 230837 Amtsgericht München" → "230837". Die Registerart steht in einem
 * eigenen select-Feld und wird hier NICHT mitgeschrieben — feldübergreifende
 * Effekte gehören nicht in eine reine Prüffunktion.
 */
export function normalizeHrNummer(input) {
    const raw = asText(input);
    if (!raw)
        return "";
    const match = raw.match(HR_REGISTER_RE);
    const rest = match ? match[3] : raw;
    // Angehängten Klartext (Gerichtsname, >= 2 Buchstaben am Stück) abschneiden.
    const number = rest.trim().split(/\s+[A-Za-z]{2,}/)[0].trim();
    // Der in Berlin uebliche Registerzusatz steht durch ein Leerzeichen
    // getrennt hinter der Nummer ("230837 B") und gehoert dazu.
    return HR_NUMBER_RE.test(number) ? number.replace(/\s+/g, "") : raw;
}
/** Weich: die Nummernkreise sind nicht bundesweit einheitlich. */
export const isValidHrNummer = (input) => isEmpty(input) || HR_NUMBER_RE.test(normalizeHrNummer(input));
/**
 * Geschlossene Registry: durch `satisfies Record<FieldFormat, …>` kompiliert
 * ein neues Format ohne Spezifikation nicht — und zwar an genau einer Zeile.
 */
export const FIELD_FORMAT_SPECS = {
    phone: {
        zweck: "Telefon, Mobil und Fax; Speicherform \"+49 221 12345\" (mit Leerzeichen)",
        isValid: isValidPhone,
        normalize: normalizePhone,
        message: "Bitte eine gültige Telefonnummer eingeben (z. B. 0221 12345 oder +49 221 12345)",
        defaultSeverity: "error",
        inputMode: "tel",
    },
    email: {
        zweck: "E-Mail-Adresse",
        isValid: isValidEmail,
        normalize: normalizeEmail,
        message: "Bitte eine gültige E-Mail-Adresse eingeben",
        defaultSeverity: "error",
        inputMode: "email",
    },
    url: {
        zweck: "Webseite; schemalose Eingabe wird um https:// ergänzt",
        isValid: isValidUrl,
        normalize: normalizeUrl,
        message: "Bitte eine gültige Web-Adresse eingeben (z. B. www.beispiel.de)",
        defaultSeverity: "error",
        inputMode: "url",
    },
    integer: {
        zweck: "Stück- und Kopfzahlen (Mitarbeiter, Fahrzeuge)",
        isValid: isValidInteger,
        normalize: normalizeInteger,
        message: "Bitte eine ganze Zahl eingeben (z. B. 20)",
        defaultSeverity: "warning",
        inputMode: "numeric",
    },
    decimal: {
        zweck: "Dezimalzahl ohne feste Einheit (Monatsstunden)",
        isValid: isValidDecimal,
        normalize: normalizeDecimal,
        message: "Bitte eine Zahl eingeben (z. B. 173,33)",
        defaultSeverity: "warning",
        inputMode: "decimal",
    },
    currencyEUR: {
        zweck: "Geldbetrag in Euro",
        isValid: isValidDecimal,
        normalize: normalizeDecimal,
        message: "Kein Betrag erkannt — bitte prüfen",
        defaultSeverity: "warning",
        inputMode: "decimal",
    },
    percent: {
        zweck: "Quoten und Verteilungen, 0–100",
        isValid: isValidPercent,
        normalize: normalizeDecimal,
        message: "Bitte einen Prozentwert zwischen 0 und 100 eingeben",
        defaultSeverity: "warning",
        inputMode: "decimal",
    },
    area: {
        zweck: "Fläche in m²; die Einheit steht im Label, nie im Wert",
        isValid: isValidDecimal,
        normalize: normalizeDecimal,
        message: "Bitte eine Flächenangabe eingeben (z. B. 1.250,5)",
        defaultSeverity: "warning",
        inputMode: "decimal",
    },
    year: {
        zweck: "Gründungsjahr",
        isValid: isValidYear,
        normalize: normalizeYear,
        message: "Bitte ein Jahr im Format JJJJ eingeben (z. B. 1965)",
        defaultSeverity: "error",
        inputMode: "numeric",
    },
    date: {
        zweck: "Datumsangabe, gespeichert als ISO-Datum",
        isValid: isValidDate,
        normalize: normalizeDate,
        message: "Bitte ein Datum im Format TT.MM.JJJJ eingeben",
        defaultSeverity: "error",
    },
    plz: {
        zweck: "Deutsche Postleitzahl",
        isValid: isValidPlz,
        normalize: normalizePlz,
        message: "Ungültige PLZ (5-stellig, Deutschland)",
        defaultSeverity: "error",
        inputMode: "numeric",
    },
    ustIdNr: {
        zweck: "Umsatzsteuer-Identifikationsnummer",
        isValid: isValidUstIdNr,
        normalize: normalizeUstIdNr,
        message: "Format: DE + 9 Ziffern",
        defaultSeverity: "error",
    },
    hrnummer: {
        zweck: "Handelsregisternummer ohne Registerart (weiche Prüfung)",
        isValid: isValidHrNummer,
        normalize: normalizeHrNummer,
        message: "Bitte nur die Registernummer eingeben (z. B. 230837)",
        defaultSeverity: "warning",
    },
};
export const FIELD_FORMATS = Object.keys(FIELD_FORMAT_SPECS);
export const formatSpec = (format) => FIELD_FORMAT_SPECS[format];
/** `undefined`, solange das Feld kein Format trägt — der heutige Normalfall. */
export const fieldFormatSpec = (field) => field.format ? FIELD_FORMAT_SPECS[field.format] : undefined;
/**
 * Die eine Quelle für `inputmode` — in beiden Renderern UND in den
 * verschachtelten Eingaben. Ohne `format` bleibt der Hinweis leer und das Feld
 * verhält sich wie ein normales Textfeld.
 */
export const inputModeForFormat = (format) => (format ? formatSpec(format).inputMode : undefined);
/**
 * Skalare Werte werden geprüft, verschachtelte nicht.
 *
 * `number` und `boolean` werden dabei über ihre Zeichenkette mitgeprüft und
 * NICHT durchgewunken: die Antworten liegen im jsonb, und der Token-Pfad
 * nimmt unauthentifiziert beliebiges JSON entgegen — eine als JSON-Zahl
 * gesendete Telefonnummer würde eine serverseitige Durchsetzung sonst
 * trivial aushebeln.
 *
 * Objekte und Arrays (Kontaktdaten, PersonEntry, VerantEntry) sind dagegen
 * über das `format`-Attribut gar nicht erreichbar: dort trägt jedes Teilfeld
 * ein eigenes Format, und die Meldung braucht einen Pfad statt eines flachen
 * Keys. Diese Prüfung liegt in `entryFormats.ts`
 * (`validateNestedAnswerFormats`) und ergänzt die hiesige, statt sie zu
 * ersetzen.
 */
const skalarText = (value) => {
    if (typeof value === "string")
        return value;
    if (typeof value === "number")
        return Number.isFinite(value) ? String(value) : undefined;
    if (typeof value === "boolean")
        return String(value);
    return undefined;
};
export function validateFieldFormat(field, value) {
    const spec = fieldFormatSpec(field);
    if (!spec || !field.format)
        return undefined;
    const text = skalarText(value);
    if (text === undefined || text.trim() === "")
        return undefined;
    if (spec.isValid(text))
        return undefined;
    return {
        key: field.key,
        format: field.format,
        message: spec.message,
        severity: spec.defaultSeverity,
        value: text,
    };
}
/**
 * Kanonische Form eines Einzelwerts; ohne `format` bleibt alles wie es war.
 * Ein Skalar wird dabei zur Zeichenkette — die Speicherform ist bei jedem
 * Format ein String (Invariante 3), sonst wäre "01067" nach einem Umweg über
 * eine JSON-Zahl 1067.
 */
export function normalizeFieldFormat(field, value) {
    const spec = fieldFormatSpec(field);
    if (!spec)
        return value;
    const text = skalarText(value);
    if (text === undefined || text.trim() === "")
        return value;
    return spec.normalize(text);
}
export const normalizeByFormat = (format, value) => FIELD_FORMAT_SPECS[format].normalize(value);
/**
 * Prüft einen ganzen Antwortsatz gegen die Formate eines Schemas.
 *
 * Reine Funktion: kein Zustand, keine Seiteneffekte, `answers` wird nicht
 * verändert, es wird nichts geworfen. Felder ohne `format` und Keys, die im
 * Schema nicht vorkommen, werden ignoriert — unbekannte Keys sind Sache von
 * `validateFormAnswers`, deren Signatur unverändert bleibt.
 */
export function validateAnswerFormats(schema, answers) {
    const issues = [];
    for (const field of collectFields(schema)) {
        if (!field.format)
            continue;
        if (!Object.prototype.hasOwnProperty.call(answers, field.key))
            continue;
        const issue = validateFieldFormat(field, answers[field.key]);
        if (issue)
            issues.push(issue);
    }
    return issues;
}
